// infrastructure/api/chargeMasterApi.js
import { auth } from 'boot/firebase';
import { serverTimestamp } from 'firebase/firestore';
import ChargeMastersDB from 'src/fb/chargeMasters-db';
import GenericDB from 'src/fb/generic-db';

import { VersionCollision } from '../../application/ports/versionErrors';
import { toAsOfDate } from '../../domain/services/effectiveDating';
import { toChargeMasters, toDocument, versionDocumentId } from '../mappers/chargeMasterMapper';

/**
 * Persistence for charge masters.
 *
 * `chargeMasters` is a flat root collection scoped by the document's `clinic` field, so the clinic
 * constraint is what scopes a read rather than the path.
 *
 * EVERY DOCUMENT IS A SHEET. A clinic's charge master is a series of them sharing a `chargeMasterId`,
 * each applying from its `effectiveFrom` onward. Unlike `fundingSources`, EVERY document id is
 * derived from its coordinates including the first — that collection's series head keeps an auto-id
 * because other collections already reference it, and this one is net-new with nothing pointing at
 * it. So the create path is collision-safe on the same terms as an append: two writers creating a
 * clinic's first sheet on one date aim at the same id and the rules refuse the loser. That holds only
 * because the series key is the CLINIC rather than a generated value — see `createChargeMaster`, where
 * the reasoning and the alternative that does not work are set out.
 *
 * ONE READ, BY CLINIC. A clinic has one charge master today, so "the clinic's charge master" and
 * "the series" are the same query and a second read would be the same rows under another name.
 * Resolving which sheet applies to a date is the domain's job, through `VersionTimeline`; deciding
 * whether the rows form ONE series is `soleSeriesOf`'s. Neither happens here — the collection has no
 * idea what date any caller cares about, and two callers on one screen can care about different ones.
 *
 * TOMBSTONES ARE NOT FILTERED. Version numbers are encoded in the document id and must never be
 * re-used, so the next number has to count removed sheets too; a reader that filtered them would
 * compute an id that is already occupied and the write would be refused. Resolution excludes them
 * downstream instead.
 *
 * NO COMPOSITE INDEX, and that is a consequence of the read rather than an omission. The one query
 * here is a single equality on `clinic`, which Firestore serves by merging single-field indexes. The
 * sibling `fundingSources` read needs a composite index because it adds a RANGE filter
 * (`effectiveFrom <= asOf`) — and a range filter is exactly what makes a real composite index
 * necessary. A clinic's sheets are few enough to resolve in memory, so this read stays equality-only
 * deliberately: pushing the date into the query would also EXCLUDE any document missing the field
 * rather than returning it last, which is the mechanism behind more than one outage in this pattern.
 * If a future reader (the projector resolving one sheet for a service date) does add that filter,
 * its index ships before the code does.
 */

/**
 * The effective date, normalized, or a thrown error.
 *
 * EVERY WRITE GOES THROUGH THIS, because a sheet without a readable date is the one malformed
 * document this collection cannot tolerate. `VersionTimeline` drops such a row rather than guessing
 * its position, so it would vanish from the editor while still sitting in the collection —
 * invisible, unresolvable, and impossible to correct through the UI that created it.
 *
 * Thrown rather than defaulted to today. A sheet dated by accident is worse than a save that failed:
 * the failure is visible and repeatable, and the wrong date silently decides what a past service date
 * is charged at.
 *
 * READ WITH `toAsOfDate`, NOT `toDateStr`, and the difference is a whole day. `toDateStr` truncates a
 * longer ISO string, so it would accept an instant — and slicing one picks whatever zone it was
 * written in: `'2026-09-01T23:45:00Z'` lands on 2026-09-01, already the 2nd in Tokyo. The date would
 * then be baked into the document id as well as the field, so nothing downstream could detect it.
 *
 * The asymmetry `toDateStr` exists for does not apply here. It is lenient because a BOUND is stored
 * data that may predate the convention; an `effectiveFrom` arriving at a write is caller-supplied
 * input from a date picker in this same app, so there is no legacy shape to absorb and an instant is
 * a programming error. Same reading `fundingSourceApi` gives a supplied `asOf`.
 *
 * @param {*} effectiveFrom
 * @returns {string} 'YYYY-MM-DD'
 */
const requireCalendarDate = (effectiveFrom) => {
  const dateStr = toAsOfDate(effectiveFrom);

  if (dateStr === null) {
    throw new TypeError(
      `A charge master sheet needs a 'YYYY-MM-DD' effective date; got ${JSON.stringify(effectiveFrom)}.`,
    );
  }
  return dateStr;
};

/**
 * The clinic scope, or a thrown error.
 *
 * AN UNREADABLE SCOPE IS REFUSED RATHER THAN QUERIED, because the query would succeed. Firestore
 * answers `clinic == undefined` with zero rows, and zero rows here does not read as "we could not
 * ask" — it reads as "this clinic has never made a charge master", which is a legitimate state with
 * its own screen and its own primary action. So the page would invite somebody to set up a charge
 * master the clinic may already have.
 *
 * Thrown for the same reason `requireCalendarDate` throws: a failure that surfaces is worth more than
 * an empty answer that looks like data.
 *
 * @param {*} clinicId
 * @returns {string}
 */
const requireClinicId = (clinicId) => {
  if (typeof clinicId !== 'string' || clinicId.length === 0) {
    throw new TypeError(
      `A charge master read needs a clinic; got ${JSON.stringify(clinicId)}.`,
    );
  }
  return clinicId;
};

/**
 * Tombstone fields — the platform's soft-delete trio, matching `coverageApi` and `fundingSourceApi`
 * exactly rather than coining a synonym: a retention sweep is cross-collection by nature and one
 * written against the established names would skip a tombstone called anything else.
 *
 * `deleteTimestamp` is a Firestore `Timestamp` and not a 'YYYY-MM-DD' string. `effectiveFrom` is a
 * calendar date and must never be an instant, whereas a deletion is a moment, and the clock is the
 * server's so a wrong device clock cannot move a retention window.
 */
const tombstone = () => ({
  isDeleted: true,
  deleteTimestamp: serverTimestamp(),
  // `auth` ITSELF is optional-chained, not just `currentUser`. It is a mutable export that is torn
  // down and re-initialized on a region switch, so it is null for a window — and a tombstone written
  // in that window would throw a TypeError instead of recording who deleted what.
  deletedBy: auth?.currentUser?.uid ?? null,
});

/**
 * Every sheet a clinic has, tombstones included, in no particular order.
 *
 * UNRESOLVED AND UNSORTED, deliberately. Which sheet applies depends on the date the caller cares
 * about, and ordering is a presentation decision; imposing either here would make every caller that
 * wanted something else fight it.
 *
 * @param {{ clinicId: string }} scope
 * @returns {Promise<import('../../domain/model/ChargeMaster.types').ChargeMaster[]>}
 */
export async function loadChargeMasterVersions({ clinicId }) {
  const documents = await new ChargeMastersDB()
    .readAll([['clinic', '==', requireClinicId(clinicId)]]);

  return toChargeMasters(documents);
} // loadChargeMasterVersions()

/**
 * Creates a clinic's charge master — its first sheet, on the date the user chose.
 *
 * NOT an append, and it cannot be one: `appendChargeMasterVersion` places a sheet on an existing
 * series, and there is no series yet.
 *
 * THE SERIES KEY IS THE CLINIC, NOT A GENERATED ID, and that is the whole concurrency guard on this
 * path. A generated key made the first save unraceable in the one way that mattered: two people
 * opening an empty charge master and saving at once produced two different keys, so their derived
 * document ids differed, neither transaction read what the other wrote, and BOTH creates succeeded.
 * The clinic then owned two charge masters — the `AmbiguousChargeMaster` state `soleSeriesOf`
 * (correctly) refuses to arbitrate, which no rule, no reader and nothing in the product can undo.
 *
 * A TRANSACTION CANNOT FIX THAT, which is worth stating because it is the obvious thing to reach for.
 * A transaction detects conflicts on documents it READ, and the two writers read different documents;
 * the escape — query for an existing series inside the transaction — does not exist on the client,
 * where `Transaction.get` takes a DocumentReference and has no query form. `GenericDB`'s
 * transactional `readAll` runs `getDocs` OUTSIDE the transaction, so it cannot stand in for one.
 *
 * Keyed on the clinic, both writers compute the same id, the create-only rule refuses the loser, and
 * the collision arrives as a `VersionCollision` the caller can act on. The general rule this follows:
 * A SERIES KEY IS THE IDENTITY OF THE THING THE SERIES IS ABOUT — the clinic while a clinic has one
 * charge master, and that entity's id if a series ever becomes per-funder or per-client. Both
 * contenders know it before writing, which is the only property that makes a derived id collide.
 *
 * It also degrades correctly. Two writers picking DIFFERENT dates land `{clinic}_2026-09-01_1` and
 * `{clinic}_2026-10-01_1`: one series, two dates, each with its own version 1 — the ordinary shape,
 * since `version` sequences within a date rather than across the series.
 *
 * A clinic that already holds a sheet under a generated key is unaffected: `chargeMasterId` is READ
 * from the document, so its series keeps the key it was created with and every later sheet appends to
 * it. This decides new series only, which is why it needs no migration.
 *
 * @param {import('../../application/ports/ChargeMasterRepository.port').CreateChargeMasterRequest}
 *   request
 * @returns {Promise<{ chargeMasterId: string, versionId: string }>}
 */
export function createChargeMaster({ clinicId, effectiveFrom, record }) {
  const startsOn = requireCalendarDate(effectiveFrom);
  const chargeMastersDb = new ChargeMastersDB();
  // Validated because the DOCUMENT ID now derives from it. An unreadable clinic would otherwise
  // produce `_2026-09-01_1` — a well-formed id for a sheet belonging to nobody, and one that every
  // clinic with the same problem would contend for.
  const chargeMasterId = requireClinicId(clinicId);
  const id = versionDocumentId(chargeMasterId, startsOn, 1);

  return new GenericDB().executeTransaction(async (transaction) => {
    const occupied = await transaction.read(chargeMastersDb, { id });

    // BOTH EMPTY VALUES, because the two backends disagree about what "missing" is: the Firestore
    // path returns null and the IndexedDB path hands back `undefined` straight from the store.
    if (occupied !== null && occupied !== undefined) {
      throw new VersionCollision(startsOn, 1);
    }

    await transaction.create(chargeMastersDb, {
      data: {
        ...toDocument(record),
        chargeMasterId,
        clinic: clinicId,
        effectiveFrom: startsOn,
        version: 1,
        isLatestVersion: true,
        isDeleted: false,
      },
      id,
    });

    return { chargeMasterId, versionId: id };
  });
} // createChargeMaster()

/**
 * Appends one sheet, with everything else the same save has to change, in a single transaction.
 *
 * THE TRANSACTION EXISTS FOR THE READ, not for the writes. Its one read is the document this save
 * intends to create, looked up by the id derived from its coordinates — which is the only form of
 * read that works here: `GenericDB`'s transactional `readAll` runs `getDocs` OUTSIDE the transaction
 * before re-reading each hit through it, so a document created concurrently is invisible to both
 * writers and neither detects the other. A version number computed from a query inside a transaction
 * is therefore not protected by it, however transactional the call looks.
 *
 * THE RULES ARE THE REAL GUARD, and this check is what makes their refusal legible. A losing writer
 * aims at a document that now exists, so `transaction.set` is evaluated as an update and denied —
 * arriving as `permission-denied`, indistinguishable from "you are not a manager at this clinic".
 * Reading first turns the recoverable case into a `VersionCollision` a retry loop can act on.
 *
 * THREE KINDS OF WRITE, ONE DECISION. The new sheet; the flag cleared on whatever it supersedes at
 * the same date; and tombstones for a sheet scheduled on another date that this save replaces.
 * Landing the sheet without clearing the flag leaves the as-of query returning either row for that
 * date.
 *
 * @param {import('../../application/ports/ChargeMasterRepository.port').AppendSheetRequest} request
 * @returns {Promise<string>} the new sheet's document id
 */
export function appendChargeMasterVersion({
  chargeMasterId,
  clinicId,
  effectiveFrom,
  version,
  record,
  supersededVersionIds = [],
  cancelledVersionIds = [],
}) {
  const startsOn = requireCalendarDate(effectiveFrom);
  const chargeMastersDb = new ChargeMastersDB();
  // Built from the NORMALIZED date, so the id and the field can never disagree about when this
  // sheet applies.
  const id = versionDocumentId(chargeMasterId, startsOn, version);

  return new GenericDB().executeTransaction(async (transaction) => {
    // Reads before writes, per Firestore's transaction rule — and there is only one.
    const occupied = await transaction.read(chargeMastersDb, { id });

    if (occupied !== null && occupied !== undefined) {
      throw new VersionCollision(startsOn, version);
    }

    await transaction.create(chargeMastersDb, {
      data: {
        ...toDocument(record),
        chargeMasterId,
        clinic: clinicId,
        effectiveFrom: startsOn,
        version,
        isLatestVersion: true,
        isDeleted: false,
      },
      id,
    });

    // Bookkeeping, not a decision: an earlier sheet for this same date is still true history, it is
    // simply no longer the one to read. It keeps its row and loses only the flag.
    await Promise.all(supersededVersionIds.map((supersededId) => {
      return transaction.update(chargeMastersDb, {
        data: { id: supersededId, isLatestVersion: false },
      });
    }));

    // A decision the user was warned about: a sheet scheduled for another date is canceled
    // outright. Only sheets that have never been in force reach here — the rules refuse the rest.
    await Promise.all(cancelledVersionIds.map((cancelledId) => {
      return transaction.update(chargeMastersDb, { data: { id: cancelledId, ...tombstone() } });
    }));

    return id;
  });
} // appendChargeMasterVersion()
